/** Form-level and tab-level settings editor. */
import { useEffect, useMemo, useState } from 'react';
import type { DynamicFormDefinition, FormTab } from '../../models/form';
import { useFeedback } from '../../components/Feedback';
import { createLayout } from '../../legacy/schema-codec';
import { normalizeFormHeaderBackgroundColor, normalizeFormTitleColor } from '../../utils/form-settings';
import { PersianDateTimeInput } from '../renderer/PersianDateTimeInput';
import { useAccess } from '../access/AccessContext';
import { useUiLocale } from '../../app/ui-locale';

interface Props {
  form: DynamicFormDefinition;
  activeTab: string;
  onChange: (form: DynamicFormDefinition) => void;
  onActiveTabChange: (tab: string) => void;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return <label className="df-property-field"><span>{label}</span>{children}{hint ? <small className="help-block">{hint}</small> : null}</label>;
}


function StageColorField({ label, value, onChange, fallback, disabled = false }: { label: string; value?: string; onChange: (value?: string) => void; fallback: string; disabled?: boolean }) {
  const normalized = /^#[0-9a-f]{6}$/i.test(value ?? '') ? value! : fallback;
  return <Field label={label}>
    <span className="df-stage-color-field">
      <input type="color" disabled={disabled} value={normalized} aria-label={`${label}؛ انتخاب رنگ`} onChange={(event) => onChange(event.target.value)} />
      <input className="form-control dir-ltr" disabled={disabled} value={value ?? ''} placeholder={fallback} onChange={(event) => {
        const next = event.target.value.trim();
        onChange(next || undefined);
      }} />
      {value ? <button type="button" className="btn btn-xs btn-default" disabled={disabled} onClick={() => onChange(undefined)}>پاک کردن</button> : null}
    </span>
  </Field>;
}

function nextTabName(tabs: FormTab[]): string {
  const used = new Set(tabs.map((tab) => tab.name));
  let index = 1;
  while (used.has(`Page${index}`)) index += 1;
  return `Page${index}`;
}

function countTabContent(tab: FormTab): number {
  const countRows = (rows: FormTab['rows']): number => rows.reduce((total, row) => total + row.columns.reduce((columnTotal, column) => columnTotal + column.fields.length + countRows(column.rows ?? []), 0), 0);
  return countRows(tab.rows);
}

function splitRecipients(value: string): string[] {
  return value.split(/[,;،؛\r\n]+/).map((item) => item.trim()).filter(Boolean);
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.append(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Copy failed');
}

export function FormSettings({ form, activeTab, onChange, onActiveTabChange }: Props) {
  const { hasFeature } = useAccess();
  const { notify } = useFeedback();
  const { locale } = useUiLocale();
  const uiText = (fa: string, en: string, fr: string) => locale === 'fr' ? fr : locale === 'en' ? en : fa;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [formKeyEditUnlocked, setFormKeyEditUnlocked] = useState(false);
  const [formKeyDraft, setFormKeyDraft] = useState(form.Name);
  const patch = (changes: Partial<DynamicFormDefinition>) => onChange({ ...form, ...changes });
  const activeIndex = form.Tabs.findIndex((tab) => tab.name === activeTab);
  const active = form.Tabs[activeIndex] ?? form.Tabs[0];
  const recipients = useMemo(() => splitRecipients(form.SmsRecipients), [form.SmsRecipients]);
  const persistedForm = Boolean(String(form.Id ?? '').trim()) && String(form.Id) !== '0';
  const normalizedFormKeyDraft = formKeyDraft.replace(/[^A-Za-z0-9_]/g, '');
  const formKeyFormatValid = /^[A-Za-z][A-Za-z0-9_]*$/.test(normalizedFormKeyDraft);
  useEffect(() => {
    if (!formKeyEditUnlocked) setFormKeyDraft(form.Name);
  }, [form.Name, formKeyEditUnlocked]);

  useEffect(() => {
    setFormKeyEditUnlocked(false);
    setFormKeyDraft(form.Name);
  }, [form.Id]);

  const copyFormKey = async () => {
    try {
      await copyText(form.Name);
      notify(uiText('کلید فنی فرم کپی شد.', 'Form key copied.', 'Clé technique du formulaire copiée.'), 'success');
    } catch {
      notify(uiText('کپی کلید فنی فرم انجام نشد.', 'The form key could not be copied.', 'Impossible de copier la clé technique du formulaire.'), 'error');
    }
  };

  const unlockFormKeyEditing = (checked: boolean) => {
    if (!checked) {
      setFormKeyEditUnlocked(false);
      setFormKeyDraft(form.Name);
      return;
    }
    const accepted = window.confirm(uiText(
      'تغییر کلید فنی فرم می‌تواند مسیر فیلدها، فیلترها و گزارش‌های وابسته را مختل کند. فقط پیش از اولین ذخیره فرم و با آگاهی کامل ادامه دهید.',
      'Changing the form key can break field paths, filters and reports. Continue only before the form is saved for the first time and only if you understand the impact.',
      'La modification de la clé technique du formulaire peut rompre les chemins de champs, les filtres et les rapports. Continuez uniquement avant le premier enregistrement du formulaire et si vous en comprenez les conséquences.'
    ));
    if (!accepted) return;
    setFormKeyDraft(form.Name);
    setFormKeyEditUnlocked(true);
  };

  const applyFormKeyDraft = () => {
    if (!formKeyFormatValid) {
      notify(uiText('کلید فنی فرم باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.', 'The form key must start with an English letter and contain only letters, numbers and underscores.', 'La clé technique du formulaire doit commencer par une lettre anglaise et ne contenir que des lettres, des chiffres et des traits de soulignement.'), 'warning');
      return;
    }
    patch({ Name: normalizedFormKeyDraft });
    setFormKeyEditUnlocked(false);
    notify(uiText('کلید فنی فرم به‌روزرسانی شد.', 'The form key was updated.', 'La clé technique du formulaire a été mise à jour.'), 'success');
  };

  const updateTab = (changes: Partial<FormTab>) => {
    if (!active) return;
    onChange({ ...form, Tabs: form.Tabs.map((tab) => tab.name === active.name ? { ...tab, ...changes } : tab) });
  };
  const addTab = () => {
    const name = nextTabName(form.Tabs);
    const number = form.Tabs.length + 1;
    const tabTitle = locale === 'fr' ? `Étape ${number}` : locale === 'en' ? `Step ${number}` : `مرحله ${number}`;
    const tab: FormTab = { name, title: tabTitle, isReturnEnable: number > 1, isPreviewEnable: false, color: '', textColor: '', rows: [createLayout(1)] };
    onChange({ ...form, Tabs: [...form.Tabs, tab] });
    onActiveTabChange(tab.name);
  };
  const removeTab = () => {
    if (!active || form.Tabs.length <= 1) return;
    const contentCount = countTabContent(active);
    if (contentCount && !window.confirm(`این مرحله دارای ${contentCount} کنترل یا لایه است. مرحله حذف شود؟`)) return;
    if (!contentCount && !window.confirm('این مرحله حذف شود؟')) return;
    const next = form.Tabs.filter((tab) => tab.name !== active.name);
    const nextActive = next[Math.min(Math.max(0, activeIndex), next.length - 1)] ?? next[0];
    onChange({ ...form, Tabs: next });
    onActiveTabChange(nextActive?.name ?? 'Page1');
  };

  return <aside className="df-form-settings">
    <h3>تنظیمات فرم</h3>
    <div className="df-property-list">
      <Field label="عنوان فرم"><input className="form-control" value={form.Title} onChange={(event) => patch({ Title: event.target.value })} /></Field>
      <Field label="توضیحات"><textarea className="form-control" rows={3} value={form.Description} onChange={(event) => patch({ Description: event.target.value })} /></Field>
      <section className="df-form-display-settings" aria-labelledby="df-form-heading-settings-title">
        <h4 id="df-form-heading-settings-title">تنظیمات نمایش سربرگ فرم</h4>
        <label className="df-checkbox-row"><input type="checkbox" checked={form.ShowHeader} onChange={(event) => patch({ ShowHeader: event.target.checked })} /> نمایش کامل بخش سربرگ فرم</label>
        <div className={!form.ShowHeader ? 'df-form-heading-fields is-disabled' : 'df-form-heading-fields'} aria-disabled={!form.ShowHeader}>
          <div className="df-two-column-fields">
            <Field label="تراز عنوان">
              <select className="form-control" disabled={!form.ShowHeader} value={form.TitleAlignment} onChange={(event) => patch({ TitleAlignment: event.target.value as DynamicFormDefinition['TitleAlignment'] })}>
                <option value="inherit">پیش‌فرض قالب سایت</option>
                <option value="right">راست‌چین</option>
                <option value="center">وسط‌چین</option>
                <option value="left">چپ‌چین</option>
              </select>
            </Field>
            <StageColorField label="رنگ عنوان" disabled={!form.ShowHeader} value={form.TitleColor || undefined} fallback="#1f365c" onChange={(TitleColor) => patch({ TitleColor: TitleColor ?? '' })} />
            <StageColorField label="رنگ پس‌زمینه سربرگ" disabled={!form.ShowHeader} value={form.HeaderBackgroundColor || undefined} fallback="#eeeeee" onChange={(HeaderBackgroundColor) => patch({ HeaderBackgroundColor: HeaderBackgroundColor ?? '' })} />
          </div>
          <div className="df-form-title-preview" aria-label="پیش‌نمایش عنوان فرم" style={{ backgroundColor: normalizeFormHeaderBackgroundColor(form.HeaderBackgroundColor) || undefined }}>
            <small>پیش‌نمایش</small>
            <strong style={{ textAlign: form.TitleAlignment === 'inherit' ? undefined : form.TitleAlignment, color: normalizeFormTitleColor(form.TitleColor) || undefined }}>{form.Title || 'عنوان فرم'}</strong>
          </div>
          <label className="df-checkbox-row"><input type="checkbox" disabled={!form.ShowHeader} checked={form.ShowDateRange} onChange={(event) => patch({ ShowDateRange: event.target.checked })} /> نمایش بازه زمانی در سربرگ فرم</label>
        </div>
        <p className="help-block">مخفی‌کردن کامل سربرگ فقط عنوان، توضیحات، بازه زمانی و وضعیت ظرفیت را از صفحه عمومی حذف می‌کند؛ محدودیت‌های تاریخ و ظرفیت همچنان در زمان ثبت اجرا می‌شوند.</p>
      </section>
      <label className="df-checkbox-row"><input type="checkbox" checked={form.Enable.toLowerCase() === 'true'} onChange={(event) => patch({ Enable: event.target.checked ? 'True' : 'False' })} /> فرم فعال باشد</label>
      <label className="df-checkbox-row"><input type="checkbox" checked={form.IsInfinite} onChange={(event) => patch({ IsInfinite: event.target.checked })} /> بدون محدودیت زمانی</label>
      <div className="df-two-column-fields">
        <Field label="تاریخ شروع"><PersianDateTimeInput id="df-form-start-date" name="StartDate" kind="tdate" className="form-control dir-ltr" disabled={form.IsInfinite} value={form.StartDate} maxDate={form.EndDate} onChange={(StartDate) => patch({ StartDate })} placeholder="1405/01/01" /></Field>
        <Field label="تاریخ پایان"><PersianDateTimeInput id="df-form-end-date" name="EndDate" kind="tdate" className="form-control dir-ltr" disabled={form.IsInfinite} value={form.EndDate} minDate={form.StartDate} onChange={(EndDate) => patch({ EndDate })} placeholder="1406/01/01" /></Field>
      </div>
      <Field label="پیام عدم دسترسی/انقضا"><input className="form-control" value={form.ExpireMessage} onChange={(event) => patch({ ExpireMessage: event.target.value })} /></Field>
      {hasFeature('Forms.Capacity') ? <label className="df-checkbox-row"><input type="checkbox" checked={form.HaveCapacity} onChange={(event) => patch({ HaveCapacity: event.target.checked })} /> ظرفیت محدود</label> : null}
      {hasFeature('Forms.Capacity') && form.HaveCapacity ? <div className="df-subsettings"><Field label="ظرفیت"><input className="form-control" type="number" min={1} value={form.Capacity} onChange={(event) => patch({ Capacity: Math.max(1, Number(event.target.value) || 1) })} /></Field><label className="df-checkbox-row"><input type="checkbox" checked={form.InstantSuspension} onChange={(event) => patch({ InstantSuspension: event.target.checked })} /> توقف آنی پس از تکمیل ظرفیت</label><Field label="پیام ظرفیت"><input className="form-control" value={form.CapacityMessage} onChange={(event) => patch({ CapacityMessage: event.target.value })} /></Field></div> : null}
      <label className="df-checkbox-row"><input type="checkbox" checked={form.ShowFinalStage} onChange={(event) => patch({ ShowFinalStage: event.target.checked })} /> نمایش پیام نهایی</label>
      <label className="df-checkbox-row"><input type="checkbox" checked={form.ShowSubmitCode} onChange={(event) => patch({ ShowSubmitCode: event.target.checked })} /> نمایش کد پیگیری</label>
      {hasFeature('Sms.Notification') ? <Field label="گیرندگان پیامک" hint={`${recipients.length.toLocaleString('fa-IR')} گیرنده؛ شماره‌ها را با کاما، نقطه‌ویرگول یا خط جدید جدا کنید.`}><textarea className="form-control dir-ltr" rows={4} value={form.SmsRecipients} onChange={(event) => patch({ SmsRecipients: event.target.value })} placeholder={'0912...\n0935...'} /></Field> : null}
    </div>


    <div className="df-settings-title">
      <h4>تنظیمات پیشرفته فرم</h4>
      <button type="button" className="btn btn-xs btn-default" onClick={() => setAdvancedOpen((current) => !current)}>{advancedOpen ? 'بستن' : 'نمایش'}</button>
    </div>
    {advancedOpen ? <div className="df-property-list">
      <section className="df-data-key-card" aria-labelledby="df-form-data-key-title">
        <div className="df-data-key-card__header">
          <div>
            <strong id="df-form-data-key-title">کلید فنی فرم</strong>
            <p>شناسه داخلی فرم و پیشوند مسیرهای داده، فیلترها و گزارش‌ها است. پس از اولین ذخیره نباید تغییر کند.</p>
          </div>
          <button type="button" className="btn btn-xs btn-default" onClick={() => void copyFormKey()}>کپی</button>
        </div>
        <input className="form-control dir-ltr df-data-key-card__value" value={form.Name} readOnly aria-readonly="true" />
        {persistedForm ? <div className="alert alert-warning df-compact-alert df-data-key-card__notice">
          این فرم قبلاً ذخیره شده است. تغییر کلید فنی فرم از این صفحه غیرفعال است و فقط باید همراه Migration داده انجام شود.
        </div> : <>
          <label className="df-checkbox-row"><input type="checkbox" checked={formKeyEditUnlocked} onChange={(event) => unlockFormKeyEditing(event.target.checked)} /> فعال‌کردن ویرایش تخصصی کلید فرم</label>
          {formKeyEditUnlocked ? <div className="df-data-key-expert-editor">
            <Field label="کلید فنی جدید فرم" hint="پس از ذخیره فرم یا ساخت فیلتر و گزارش، تغییر این مقدار می‌تواند وابستگی‌ها را نامعتبر کند.">
              <input
                className={`form-control dir-ltr ${!formKeyFormatValid ? 'is-invalid' : ''}`}
                value={formKeyDraft}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setFormKeyDraft(event.target.value.replace(/[^A-Za-z0-9_]/g, ''))}
              />
            </Field>
            {!formKeyFormatValid ? <p className="help-block text-danger">کلید فرم باید با حرف انگلیسی شروع شود و فقط شامل حروف، عدد و زیرخط باشد.</p> : null}
            <div className="df-data-key-expert-editor__actions">
              <button type="button" className="btn btn-primary btn-sm" disabled={!formKeyFormatValid || normalizedFormKeyDraft === form.Name} onClick={applyFormKeyDraft}>اعمال کلید جدید</button>
              <button type="button" className="btn btn-default btn-sm" onClick={() => { setFormKeyEditUnlocked(false); setFormKeyDraft(form.Name); }}>انصراف</button>
            </div>
          </div> : null}
        </>}
      </section>
    </div> : null}

    <div className="df-settings-title"><h4>مراحل فرم</h4>{hasFeature('Designer.MultiStep') ? <button type="button" className="btn btn-xs btn-success" onClick={addTab}>افزودن مرحله</button> : null}</div>
    {active ? <div className="df-property-list">
      <div className="df-tab-identity"><span>شناسه فنی</span><code>{active.name}</code></div>
      <Field label="عنوان مرحله"><input className="form-control" value={active.title} onChange={(event) => updateTab({ title: event.target.value })} /></Field>
      <div className="df-two-column-fields">
        <StageColorField label="رنگ مرحله" value={active.color} fallback="#337ab7" onChange={(color) => updateTab({ color })} />
        <StageColorField label="رنگ متن مرحله" value={active.textColor} fallback="#ffffff" onChange={(textColor) => updateTab({ textColor })} />
      </div>
      <label className="df-checkbox-row"><input type="checkbox" checked={active.isReturnEnable} onChange={(event) => updateTab({ isReturnEnable: event.target.checked })} /> امکان بازگشت از این مرحله</label>
      <label className="df-checkbox-row"><input type="checkbox" checked={active.isPreviewEnable} onChange={(event) => updateTab({ isPreviewEnable: event.target.checked })} /> پیش‌نمایش اطلاعات پس از این مرحله</label>
      <button type="button" className="btn btn-danger btn-sm btn-block" disabled={form.Tabs.length <= 1} onClick={removeTab}>حذف این مرحله</button>
    </div> : null}

    {hasFeature('Submissions.Status') ? <><div className="df-settings-title"><h4>وضعیت‌های پاسخ</h4><button type="button" className="btn btn-xs btn-success" onClick={() => patch({ Status: [...form.Status, { ID: -1, Title: locale === 'fr' ? `Statut ${form.Status.length + 1}` : locale === 'en' ? `Status ${form.Status.length + 1}` : `وضعیت ${form.Status.length + 1}` }] })}>افزودن</button></div>
    <div className="df-status-list">{form.Status.map((status, index) => <div key={`${status.ID}-${index}`}><input className="form-control" value={status.Title} aria-label={`عنوان وضعیت ${index + 1}`} onChange={(event) => patch({ Status: form.Status.map((item, itemIndex) => itemIndex === index ? { ...item, Title: event.target.value } : item) })} /><button type="button" className="btn btn-xs btn-danger" aria-label={`حذف وضعیت ${status.Title}`} onClick={() => patch({ Status: form.Status.filter((_, itemIndex) => itemIndex !== index) })}>حذف</button></div>)}</div></> : null}
  </aside>;
}
